// frontend/src/widgets/ANPRSightingReviewWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-05-12
// Updated:      2026-09-28 10:36 BST
//
// Description:
//   ANPR sighting human-review widget.
//   Surfaces suspect ANPR read candidates and allows authorised users to:
//     - review suspect reads
//     - confirm valid reads
//     - correct plate values
//     - quarantine/exclude sightings from reporting
//     - restore excluded sightings
//     - request DVLA retry
//     - hard-delete sightings where backend permissions allow
//
// Important:
//   - Tenant is resolved server-side only.
//   - This widget never sends tenant_id.
//   - Uses existing bearer token from localStorage key "anpr_token".
//   - Backend route: /api/anpr/sightings.
//   - Static ANPR images are served by FastAPI under /static/uploads.
//   - Local frontend on localhost:8010 rewrites /static/uploads to localhost:8020.
//
// Mobile Responsiveness (2026-09-25):
//   - Preserves the existing desktop two-pane review experience unchanged.
//   - Converts candidate table rows into labelled mobile cards below 768px without mounting
//     a duplicate candidate list or introducing a second data-fetch path.
//   - Measures the first candidate card and uses that exact height for a one-record
//     mobile selector viewport with scroll snapping.
//   - Selecting a mobile candidate opens the existing desktop detail content as an accordion
//     immediately beneath the selector instead of placing detail after the full candidate list.
//   - Provides a Back to candidates control that collapses the mobile detail accordion.
//   - Expands the nearest fixed-height mobile page host to the widget's natural content height,
//     including non-ReactGridLayout mobile wrappers used by Reporting and Cameras pages.
//   - Leaves authentication, tenant resolution, API routes, review actions, audit logic and
//     desktop two-column behaviour unchanged.
//
// Desktop Resize Fix (2026-09-25):
//   - Mobile host-height overrides are now applied only below 768px.
//   - Desktop ReactGridLayout height is never rewritten during ordinary resize activity.
//   - The pre-mobile host height/min-height are captured only when entering mobile mode and
//     restored only when leaving mobile mode or unmounting while a mobile override is active.
//
// Review Resolution Update (2026-09-28):
//   - Adds Confirm Foreign Plate using the existing backend mark_foreign review action.
//   - Confirm Valid and Confirm Foreign Plate are terminal review decisions; after the backend
//     records them, the resolved sighting disappears from the review queue but remains a base
//     ANPR sighting for reporting.
//
// Mobile Review Stability (2026-09-28):
//   - Draft filter inputs are separated from the last applied filter; typing does not refetch.
//   - The applied filter is retained in sessionStorage and reused after review actions/remounts.
//   - Mobile page hosts are switched once to natural height instead of continuously measured/re-written.
//   - Review actions preserve candidate scroll position and the mobile viewport anchor.
//   - Hard delete removes the reviewed row locally and advances to the next candidate without
//     discarding the applied filter or rebuilding the whole review queue.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const API_BASE = "/api/anpr/sightings";

const SOURCE_OPTIONS = [
  { value: "all", label: "All Sources" },
  { value: "quality_review", label: "Quality Review" },
  { value: "foreign_candidates", label: "Foreign Candidates" },
];

const ACTIONS = {
  CONFIRM: "confirm_valid",
  CONFIRM_FOREIGN: "mark_foreign",
  NOTE: "review_note",
};

const MOBILE_BREAKPOINT = 768;
const MOBILE_EMPTY_CANDIDATE_HEIGHT = 64;
const FILTER_STORAGE_KEY = "anprSightingReviewFilters";

const DEFAULT_FILTERS = {
  source: "all",
  jobId: "",
  plate: "",
  recommendation: "",
  category: "",
  includeExcluded: false,
};

function readStoredFilters() {
  if (typeof window === "undefined") return DEFAULT_FILTERS;

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(FILTER_STORAGE_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return DEFAULT_FILTERS;

    return {
      source: SOURCE_OPTIONS.some((option) => option.value === parsed.source)
        ? parsed.source
        : DEFAULT_FILTERS.source,
      jobId: typeof parsed.jobId === "string" ? parsed.jobId : "",
      plate: typeof parsed.plate === "string" ? parsed.plate : "",
      recommendation:
        typeof parsed.recommendation === "string" ? parsed.recommendation : "",
      category: typeof parsed.category === "string" ? parsed.category : "",
      includeExcluded: Boolean(parsed.includeExcluded),
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

const MOBILE_STYLES = `
  @media (max-width: 767px) {
    .anpr-sighting-review-widget {
      padding: 12px !important;
    }

    .anpr-review-header {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 12px !important;
      margin-bottom: 14px !important;
    }

    .anpr-review-header h2 {
      font-size: 1.2rem !important;
      line-height: 1.35 !important;
    }

    .anpr-review-header button {
      width: 100% !important;
      min-height: 44px !important;
    }

    .anpr-review-filter-grid {
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 8px !important;
    }

    .anpr-review-filter-grid select,
    .anpr-review-filter-grid input:not([type="checkbox"]) {
      min-height: 44px !important;
      font-size: 16px !important;
    }

    .anpr-review-filter-grid label {
      min-height: 44px !important;
      padding: 8px 2px !important;
    }

    .anpr-review-filter-grid input[type="checkbox"],
    .anpr-review-retry-label input[type="checkbox"] {
      width: 20px !important;
      height: 20px !important;
      flex: 0 0 auto !important;
    }

    .anpr-review-main-grid {
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 12px !important;
    }

    .anpr-review-candidate-shell {
      width: 100% !important;
      overflow-x: hidden !important;
      overflow-y: auto !important;
      overscroll-behavior: contain !important;
      scroll-snap-type: y mandatory !important;
      -webkit-overflow-scrolling: touch;
    }

    .anpr-review-candidate-table,
    .anpr-review-candidate-table tbody {
      display: block !important;
      width: 100% !important;
    }

    .anpr-review-candidate-table thead {
      display: none !important;
    }

    .anpr-review-candidate-row {
      display: block !important;
      width: 100% !important;
      box-sizing: border-box !important;
      scroll-snap-align: start !important;
      scroll-snap-stop: always !important;
    }

    .anpr-review-candidate-row td {
      display: grid !important;
      grid-template-columns: minmax(92px, 0.42fr) minmax(0, 0.58fr) !important;
      gap: 10px !important;
      align-items: start !important;
      width: 100% !important;
      box-sizing: border-box !important;
      padding: 8px 10px !important;
      white-space: normal !important;
      overflow-wrap: anywhere !important;
      border-bottom: 1px solid rgba(209, 213, 219, 0.72);
    }

    .anpr-review-candidate-row td:last-child {
      border-bottom: 0;
    }

    .anpr-review-candidate-row td::before {
      content: attr(data-mobile-label);
      color: #6b7280;
      font-size: 11px;
      font-weight: 700;
      line-height: 1.4;
    }

    .anpr-review-empty-row {
      display: block !important;
      width: 100% !important;
    }

    .anpr-review-empty-row td {
      display: block !important;
      width: 100% !important;
      box-sizing: border-box !important;
      padding: 18px 12px !important;
      white-space: normal !important;
      text-align: center !important;
    }

    .anpr-review-empty-row td::before {
      display: none !important;
    }

    .anpr-review-detail-pane {
      display: none !important;
      min-height: 0 !important;
      padding: 12px !important;
    }

    .anpr-review-detail-pane.is-mobile-open {
      display: block !important;
    }

    .anpr-review-mobile-detail-header {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 10px !important;
      margin-bottom: 12px !important;
      padding-bottom: 10px !important;
      border-bottom: 1px solid rgba(209, 213, 219, 0.72) !important;
    }

    .anpr-review-mobile-detail-header button {
      min-height: 40px !important;
      padding: 8px 10px !important;
      flex: 0 0 auto !important;
    }

    .anpr-review-info-grid,
    .anpr-review-preview-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .anpr-review-info-grid > div,
    .anpr-review-audit-list > div {
      min-width: 0 !important;
      overflow-wrap: anywhere !important;
    }

    .anpr-review-detail-pane input:not([type="checkbox"]),
    .anpr-review-detail-pane textarea {
      min-height: 44px !important;
      font-size: 16px !important;
    }

    .anpr-review-retry-label {
      min-height: 44px !important;
      margin-top: 8px !important;
    }

    .anpr-review-actions {
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) !important;
      gap: 8px !important;
    }

    .anpr-review-actions button {
      width: 100% !important;
      min-height: 44px !important;
    }

    .anpr-review-audit-list {
      min-width: 0 !important;
    }
  }
`;

function formatDateTime(value) {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function getStaticAssetBase() {
  const explicitStaticBase = process.env.REACT_APP_STATIC_BASE;
  if (explicitStaticBase) return explicitStaticBase.replace(/\/$/, "");

  const apiBase = process.env.REACT_APP_API_BASE;
  if (apiBase && apiBase.startsWith("http")) {
    return apiBase.replace(/\/api\/?$/, "").replace(/\/$/, "");
  }

  const hostname = window.location.hostname;
  const port = window.location.port;

  if ((hostname === "localhost" || hostname === "127.0.0.1") && port === "8010") {
    return "http://localhost:8020";
  }

  return "";
}

function buildImageUrl(path) {
  if (!path) return null;

  const cleanPath = String(path).trim();
  if (!cleanPath) return null;

  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    return cleanPath;
  }

  if (cleanPath.startsWith("/static/")) {
    const staticBase = getStaticAssetBase();
    return staticBase ? `${staticBase}${cleanPath}` : cleanPath;
  }

  return cleanPath;
}

function getToken() {
  return localStorage.getItem("anpr_token");
}

async function apiRequest(path, options = {}) {
  const token = getToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  const text = await response.text();

  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(
      data?.detail ||
        data?.message ||
        text ||
        `Request failed: ${response.status}`
    );
  }

  return data;
}

export default function ANPRSightingReviewWidget() {
  const { theme } = useTheme();

  const rootRef = useRef(null);
  const candidateShellRef = useRef(null);
  const detailPaneRef = useRef(null);
  const firstCandidateRef = useRef(null);
  const initialFiltersRef = useRef(readStoredFilters());
  const [mobileCandidateHeight, setMobileCandidateHeight] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const [source, setSource] = useState(initialFiltersRef.current.source);
  const [jobId, setJobId] = useState(initialFiltersRef.current.jobId);
  const [plate, setPlate] = useState(initialFiltersRef.current.plate);
  const [recommendation, setRecommendation] = useState(
    initialFiltersRef.current.recommendation
  );
  const [category, setCategory] = useState(initialFiltersRef.current.category);
  const [includeExcluded, setIncludeExcluded] = useState(
    initialFiltersRef.current.includeExcluded
  );
  const [appliedFilters, setAppliedFilters] = useState(initialFiltersRef.current);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [success, setSuccess] = useState("");

  const [correctPlate, setCorrectPlate] = useState("");
  const [reason, setReason] = useState("");
  const [retryAfterCorrection, setRetryAfterCorrection] = useState(true);

  const colors = useMemo(
    () => ({
      primary: theme?.primary_color || "#1f2937",
      secondary: theme?.secondary_color || "#374151",
      accent: theme?.accent_color || "#2563eb",
      bg: theme?.background_color || "#ffffff",
      text: theme?.text_color || "#111827",
      border: "#d1d5db",
      muted: "#6b7280",
      danger: "#b91c1c",
      warning: "#d97706",
      success: "#047857",
    }),
    [theme]
  );

  const buttonStyle = {
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: theme?.font_primary || "inherit",
  };

  // Mobile candidate selector: measure the actual first rendered card so the
  // viewport always exposes one complete record before the user scrolls.
  useEffect(() => {
    let frame = null;
    let observer = null;

    const measure = () => {
      if (frame) cancelAnimationFrame(frame);

      frame = requestAnimationFrame(() => {
        if (window.innerWidth >= MOBILE_BREAKPOINT) {
          setMobileCandidateHeight(null);
          return;
        }

        const firstRow = firstCandidateRef.current;
        const nextHeight = firstRow
          ? Math.ceil(firstRow.getBoundingClientRect().height)
          : MOBILE_EMPTY_CANDIDATE_HEIGHT;

        setMobileCandidateHeight((current) =>
          current === nextHeight ? current : nextHeight
        );
      });
    };

    const mobileMedia = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    measure();
    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener("change", measure);
    } else {
      mobileMedia.addListener(measure);
    }

    if (typeof ResizeObserver !== "undefined" && firstCandidateRef.current) {
      observer = new ResizeObserver(measure);
      observer.observe(firstCandidateRef.current);
    }

    return () => {
      if (mobileMedia.removeEventListener) {
        mobileMedia.removeEventListener("change", measure);
      } else {
        mobileMedia.removeListener(measure);
      }
      if (observer) observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [rows]);

  // Mobile pages intentionally remove the desktop ReactGridLayout resize handle.
  // Switch the nearest fixed-height mobile host to natural height ONCE rather than
  // continuously measuring/re-writing it. Repeated height writes during keyboard open/close
  // were causing Chrome to move the viewport while review inputs had focus.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    let activeHost = null;
    let mobileOverrideActive = false;
    let originalHeight = "";
    let originalHeightPriority = "";
    let originalMinHeight = "";
    let originalMinHeightPriority = "";
    let originalMaxHeight = "";
    let originalMaxHeightPriority = "";

    const findMobileHeightHost = () => {
      const rootRect = root.getBoundingClientRect();
      let node = root.parentElement;

      while (node && node !== document.body) {
        const rect = node.getBoundingClientRect();
        const computed = window.getComputedStyle(node);
        const inlineHeight = node.style?.height || "";
        const hasFixedInlineHeight = /^\d+(?:\.\d+)?px$/.test(inlineHeight);
        const clipsVertically =
          computed.overflow === "hidden" ||
          computed.overflowY === "hidden" ||
          computed.overflowY === "auto" ||
          computed.overflowY === "scroll";
        const widthIsRelated = Math.abs(rect.width - rootRect.width) <= 96;

        if (hasFixedInlineHeight && clipsVertically && widthIsRelated) {
          return node;
        }

        node = node.parentElement;
      }

      return root.closest(".react-grid-item");
    };

    const restoreHost = () => {
      if (!mobileOverrideActive || !activeHost) return;

      activeHost.style.setProperty("height", originalHeight, originalHeightPriority);
      activeHost.style.setProperty(
        "min-height",
        originalMinHeight,
        originalMinHeightPriority
      );
      activeHost.style.setProperty(
        "max-height",
        originalMaxHeight,
        originalMaxHeightPriority
      );

      activeHost = null;
      mobileOverrideActive = false;
    };

    const applyMobileNaturalHeight = () => {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        restoreHost();
        return;
      }

      // Keyboard/visual-viewport resize events are expected on mobile. Once the host
      // has been released to natural height, do not touch it again while still mobile.
      if (mobileOverrideActive && activeHost?.isConnected) return;

      const host = findMobileHeightHost();
      if (!host) return;

      activeHost = host;
      originalHeight = host.style.getPropertyValue("height");
      originalHeightPriority = host.style.getPropertyPriority("height");
      originalMinHeight = host.style.getPropertyValue("min-height");
      originalMinHeightPriority = host.style.getPropertyPriority("min-height");
      originalMaxHeight = host.style.getPropertyValue("max-height");
      originalMaxHeightPriority = host.style.getPropertyPriority("max-height");

      host.style.setProperty("height", "auto", "important");
      host.style.setProperty("min-height", "0px", "important");
      host.style.setProperty("max-height", "none", "important");
      mobileOverrideActive = true;
    };

    const mobileMedia = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

    applyMobileNaturalHeight();
    if (mobileMedia.addEventListener) {
      mobileMedia.addEventListener("change", applyMobileNaturalHeight);
    } else {
      mobileMedia.addListener(applyMobileNaturalHeight);
    }

    return () => {
      if (mobileMedia.removeEventListener) {
        mobileMedia.removeEventListener("change", applyMobileNaturalHeight);
      } else {
        mobileMedia.removeListener(applyMobileNaturalHeight);
      }
      restoreHost();
    };
  }, []);

  const captureReviewViewport = useCallback(() => {
    if (typeof window === "undefined") return null;

    const anchor = detailPaneRef.current || candidateShellRef.current;
    return {
      windowY: window.scrollY,
      anchor,
      anchorTop: anchor?.getBoundingClientRect().top ?? null,
      candidateScrollTop: candidateShellRef.current?.scrollTop ?? 0,
    };
  }, []);

  const restoreReviewViewport = useCallback((snapshot) => {
    if (!snapshot || typeof window === "undefined") return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (candidateShellRef.current) {
          candidateShellRef.current.scrollTop = snapshot.candidateScrollTop;
        }

        if (snapshot.anchor?.isConnected && snapshot.anchorTop !== null) {
          const nextTop = snapshot.anchor.getBoundingClientRect().top;
          const delta = nextTop - snapshot.anchorTop;
          if (Math.abs(delta) > 1) {
            window.scrollBy({ top: delta, left: 0, behavior: "auto" });
            return;
          }
        }

        window.scrollTo({ top: snapshot.windowY, left: 0, behavior: "auto" });
      });
    });
  }, []);

  const loadCandidates = useCallback(
    async (filters, { scrollToResults = false } = {}) => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();
        params.set("source", filters.source || "all");
        params.set("limit", "100");
        params.set("offset", "0");
        params.set("include_excluded", filters.includeExcluded ? "true" : "false");

        if (filters.jobId?.trim()) params.set("job_id", filters.jobId.trim());
        if (filters.plate?.trim()) params.set("plate", filters.plate.trim());
        if (filters.recommendation?.trim()) {
          params.set("recommendation", filters.recommendation.trim());
        }
        if (filters.category?.trim()) params.set("category", filters.category.trim());

        const data = await apiRequest(`/review-candidates?${params.toString()}`);
        const nextRows = data?.data || [];

        setRows(nextRows);
        setRowCount(data?.row_count || 0);

        if (scrollToResults && window.innerWidth < MOBILE_BREAKPOINT) {
          requestAnimationFrame(() => {
            candidateShellRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          });
        }

        return nextRows;
      } catch (err) {
        setError(err.message || "Failed to load review candidates.");
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadDetail = useCallback(async (sightingId) => {
    if (!sightingId) return;

    setDetailLoading(true);
    setActionError("");

    try {
      const data = await apiRequest(`/${sightingId}`);
      setDetail(data);
    } catch (err) {
      setActionError(err.message || "Failed to load sighting detail.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const applyCurrentFilters = useCallback(async () => {
    const nextFilters = {
      source,
      jobId: jobId.trim(),
      plate: plate.trim(),
      recommendation: recommendation.trim(),
      category: category.trim(),
      includeExcluded,
    };

    setAppliedFilters(nextFilters);
    setSelected(null);
    setDetail(null);
    setMobileDetailOpen(false);
    setActionError("");
    setSuccess("");

    try {
      window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(nextFilters));
    } catch {
      // Session storage is a convenience only; filtering still works without it.
    }

    if (candidateShellRef.current) {
      candidateShellRef.current.scrollTop = 0;
    }

    await loadCandidates(nextFilters, { scrollToResults: true });
  }, [
    source,
    jobId,
    plate,
    recommendation,
    category,
    includeExcluded,
    loadCandidates,
  ]);

  const handleFilterKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      applyCurrentFilters();
    },
    [applyCurrentFilters]
  );

  useEffect(() => {
    loadCandidates(initialFiltersRef.current);
  }, [loadCandidates]);

  const selectRow = async (row) => {
    setSelected(row);
    setCorrectPlate(row?.plate_normalized || "");
    setReason("");
    setDetail(null);

    if (window.innerWidth < MOBILE_BREAKPOINT) {
      setMobileDetailOpen(true);
    }

    await loadDetail(row.sighting_id);
  };

  const closeMobileDetail = () => {
    setMobileDetailOpen(false);

    requestAnimationFrame(() => {
      candidateShellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  const refreshAfterAction = async (message) => {
    const viewportSnapshot = captureReviewViewport();
    const selectedId = selected?.sighting_id;
    const selectedIndex = rows.findIndex((row) => row.sighting_id === selectedId);

    setSuccess(message);
    setActionError("");

    const nextRows = await loadCandidates(appliedFilters);

    if (!selectedId) return;

    const refreshedSelected = nextRows.find((row) => row.sighting_id === selectedId);
    const fallback =
      nextRows[Math.min(Math.max(selectedIndex, 0), Math.max(nextRows.length - 1, 0))] ||
      null;
    const nextSelected = refreshedSelected || fallback;

    if (nextSelected) {
      setSelected(nextSelected);
      setCorrectPlate(nextSelected.plate_normalized || "");
      setReason("");
      if (window.innerWidth < MOBILE_BREAKPOINT) setMobileDetailOpen(true);
      await loadDetail(nextSelected.sighting_id);
    } else {
      setSelected(null);
      setDetail(null);
      setMobileDetailOpen(false);
    }

    restoreReviewViewport(viewportSnapshot);
  };

  const postReviewAction = async (
    action,
    actionReason = "",
    successMessage = "Review action recorded."
  ) => {
    if (!selected) return;

    try {
      await apiRequest(`/${selected.sighting_id}/review-action`, {
        method: "POST",
        body: JSON.stringify({
          action,
          reason: actionReason || reason || null,
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      await refreshAfterAction(successMessage);
    } catch (err) {
      setActionError(err.message || "Failed to record action.");
    }
  };

  const quarantine = async () => {
    if (!selected) return;

    const finalReason = reason.trim();
    if (finalReason.length < 3) {
      setActionError("Quarantine requires a reason.");
      return;
    }

    try {
      await apiRequest(`/${selected.sighting_id}/quarantine`, {
        method: "POST",
        body: JSON.stringify({
          exclusion_reason: finalReason,
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      await refreshAfterAction("Sighting quarantined.");
    } catch (err) {
      setActionError(err.message || "Failed to quarantine sighting.");
    }
  };

  const restore = async () => {
    if (!selected) return;

    try {
      await apiRequest(`/${selected.sighting_id}/restore`, {
        method: "POST",
        body: JSON.stringify({
          action: "restore",
          reason: reason || "Sighting restored by reviewer.",
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      await refreshAfterAction("Sighting restored.");
    } catch (err) {
      setActionError(err.message || "Failed to restore sighting.");
    }
  };

  const correctPlateValue = async () => {
    if (!selected) return;

    if (!correctPlate.trim()) {
      setActionError("Corrected plate cannot be blank.");
      return;
    }

    try {
      await apiRequest(`/${selected.sighting_id}/correct-plate`, {
        method: "POST",
        body: JSON.stringify({
          new_plate_normalized: correctPlate,
          reason: reason || "Plate corrected by reviewer.",
          retry_dvla: retryAfterCorrection,
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      await refreshAfterAction("Plate corrected.");
    } catch (err) {
      setActionError(err.message || "Failed to correct plate.");
    }
  };

  const retryDvla = async () => {
    if (!selected) return;

    try {
      await apiRequest(`/${selected.sighting_id}/retry-dvla`, {
        method: "POST",
        body: JSON.stringify({
          reason: reason || "DVLA retry requested by reviewer.",
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      await refreshAfterAction("DVLA retry requested.");
    } catch (err) {
      setActionError(err.message || "Failed to request DVLA retry.");
    }
  };

  const hardDelete = async () => {
    if (!selected) return;

    const finalReason = reason.trim();
    if (finalReason.length < 3) {
      setActionError("Hard delete requires a reason.");
      return;
    }

    const viewportSnapshot = captureReviewViewport();
    const deletedId = selected.sighting_id;
    const deletedIndex = rows.findIndex((row) => row.sighting_id === deletedId);

    try {
      await apiRequest(`/${deletedId}`, {
        method: "DELETE",
        body: JSON.stringify({
          reason: finalReason,
          metadata: {
            source_widget: "ANPRSightingReviewWidget",
          },
        }),
      });

      const nextRows = rows.filter((row) => row.sighting_id !== deletedId);
      const nextSelected =
        nextRows[Math.min(Math.max(deletedIndex, 0), Math.max(nextRows.length - 1, 0))] ||
        null;

      setRows(nextRows);
      setRowCount((current) => Math.max(0, current - 1));
      setSuccess("Sighting hard-deleted.");
      setActionError("");
      setReason("");

      if (nextSelected) {
        setSelected(nextSelected);
        setCorrectPlate(nextSelected.plate_normalized || "");
        if (window.innerWidth < MOBILE_BREAKPOINT) setMobileDetailOpen(true);
        await loadDetail(nextSelected.sighting_id);
      } else {
        setSelected(null);
        setDetail(null);
        setMobileDetailOpen(false);
      }

      restoreReviewViewport(viewportSnapshot);
    } catch (err) {
      setActionError(err.message || "Failed to hard-delete sighting.");
    }
  };

  return (
    <div
      ref={rootRef}
      className="anpr-sighting-review-widget"
      style={{
        fontFamily: theme?.font_primary || "inherit",
        color: colors.text,
        background: colors.bg,
        padding: "18px",
        borderRadius: "14px",
        border: `1px solid ${colors.border}`,
      }}
    >
      <style>{MOBILE_STYLES}</style>

      <div
        className="anpr-review-header"
        style={{ display: "flex", justifyContent: "space-between", gap: "16px", marginBottom: "16px" }}
      >
        <div>
          <h2 style={{ margin: 0, color: colors.primary }}>ANPR Sighting Review</h2>
          <p style={{ margin: "6px 0 0", color: colors.muted }}>
            Review suspect reads, correct plates, quarantine bad data, and trigger DVLA retries.
          </p>
        </div>

        <button
          type="button"
          onClick={applyCurrentFilters}
          disabled={loading}
          style={{
            ...buttonStyle,
            background: colors.accent,
            color: "#fff",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div
        className="anpr-review-filter-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, minmax(120px, 1fr))",
          gap: "10px",
          marginBottom: "14px",
        }}
      >
        <select value={source} onChange={(e) => setSource(e.target.value)} style={inputStyle(colors)}>
          {SOURCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <input
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
          placeholder="Job ID"
          onKeyDown={handleFilterKeyDown}
          style={inputStyle(colors)}
        />

        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          placeholder="Plate"
          onKeyDown={handleFilterKeyDown}
          style={inputStyle(colors)}
        />

        <input
          value={recommendation}
          onChange={(e) => setRecommendation(e.target.value)}
          placeholder="Recommendation"
          onKeyDown={handleFilterKeyDown}
          style={inputStyle(colors)}
        />

        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Category"
          onKeyDown={handleFilterKeyDown}
          style={inputStyle(colors)}
        />

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "13px",
            color: colors.text,
          }}
        >
          <input
            type="checkbox"
            checked={includeExcluded}
            onChange={(e) => setIncludeExcluded(e.target.checked)}
          />
          Include excluded
        </label>
      </div>

      {error && <AlertBox color={colors.danger} message={error} />}
      {success && <AlertBox color={colors.success} message={success} />}
      {actionError && <AlertBox color={colors.danger} message={actionError} />}

      <div style={{ marginBottom: "10px", color: colors.muted, fontSize: "13px" }}>
        Showing {rows.length} of {rowCount} review candidates
      </div>

      <div
        className="anpr-review-main-grid"
        style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: "16px" }}
      >
        <div
          ref={candidateShellRef}
          className="anpr-review-candidate-shell"
          style={{
            overflowX: "auto",
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            ...(mobileCandidateHeight !== null
              ? { height: `${mobileCandidateHeight}px`, maxHeight: `${mobileCandidateHeight}px` }
              : {}),
          }}
        >
          <table
            className="anpr-review-candidate-table"
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}
          >
            <thead>
              <tr style={{ background: colors.primary, color: "#fff" }}>
                <Th>Plate</Th>
                <Th>Job</Th>
                <Th>Camera</Th>
                <Th>Time</Th>
                <Th>Source</Th>
                <Th>Recommendation</Th>
                <Th>Status</Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const isActive = selected?.sighting_id === row.sighting_id;

                return (
                  <tr
                    key={`${row.source}-${row.sighting_id}`}
                    ref={index === 0 ? firstCandidateRef : null}
                    className="anpr-review-candidate-row"
                    onClick={() => selectRow(row)}
                    style={{
                      cursor: "pointer",
                      background: isActive ? "rgba(37, 99, 235, 0.08)" : "#fff",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <Td mobileLabel="Plate" strong>{row.plate_normalized || "—"}</Td>
                    <Td mobileLabel="Job">{row.job_id}</Td>
                    <Td mobileLabel="Camera">{row.camera_name || row.camera_device_id || "—"}</Td>
                    <Td mobileLabel="Time">{formatDateTime(row.frame_ts_utc)}</Td>
                    <Td mobileLabel="Source">{row.source}</Td>
                    <Td mobileLabel="Recommendation">{row.recommendation || "—"}</Td>
                    <Td mobileLabel="Status">
                      {row.is_excluded ? (
                        <Badge color={colors.danger}>Excluded</Badge>
                      ) : (
                        <Badge color={colors.success}>Active</Badge>
                      )}
                    </Td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr className="anpr-review-empty-row">
                  <Td colSpan={7} mobileEmpty>
                    No review candidates found.
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          ref={detailPaneRef}
          className={`anpr-review-detail-pane${mobileDetailOpen ? " is-mobile-open" : ""}`}
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: "12px",
            padding: "14px",
            minHeight: "420px",
          }}
        >
          {selected && (
            <div
              className="anpr-review-mobile-detail-header"
              style={{ display: "none" }}
            >
              <strong style={{ color: colors.primary }}>Review details</strong>
              <button
                type="button"
                onClick={closeMobileDetail}
                style={{
                  ...buttonStyle,
                  background: "#f3f4f6",
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                }}
              >
                ← Back to candidates
              </button>
            </div>
          )}

          {!selected ? (
            <div style={{ color: colors.muted }}>Select a sighting to review.</div>
          ) : (
            <>
              <h3 style={{ marginTop: 0, color: colors.primary }}>
                {selected.plate_normalized || "Unknown Plate"}
              </h3>

              <div
                className="anpr-review-info-grid"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "13px" }}
              >
                <Info label="Sighting ID" value={selected.sighting_id} />
                <Info label="Job ID" value={selected.job_id} />
                <Info label="Camera" value={selected.camera_name || selected.camera_device_id || "—"} />
                <Info label="Read Time" value={formatDateTime(selected.frame_ts_utc)} />
                <Info label="DVLA Status" value={selected.dvla_status ?? selected.dvla_cache_status ?? "—"} />
                <Info label="Make / Colour" value={`${selected.dvla_make || "—"} / ${selected.dvla_colour || "—"}`} />
                <Info label="Category" value={selected.category || "—"} />
                <Info label="Score" value={selected.score ?? "—"} />
              </div>

              <div
                className="anpr-review-preview-grid"
                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "12px" }}
              >
                <PreviewImage label="Snapshot" url={buildImageUrl(selected.snapshot_url)} />
                <PreviewImage label="Plate" url={buildImageUrl(selected.plate_secondary_url)} />
              </div>

              <div style={{ marginTop: "12px" }}>
                <label style={labelStyle(colors)}>Correct Plate</label>
                <input
                  value={correctPlate}
                  onChange={(e) => setCorrectPlate(e.target.value.toUpperCase())}
                  style={inputStyle(colors)}
                />

                <label style={labelStyle(colors)}>Reason / Review Note</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle(colors), resize: "vertical" }}
                />

                <label
                  className="anpr-review-retry-label"
                  style={{ ...labelStyle(colors), display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="checkbox"
                    checked={retryAfterCorrection}
                    onChange={(e) => setRetryAfterCorrection(e.target.checked)}
                  />
                  Retry DVLA after correction
                </label>
              </div>

              <div
                className="anpr-review-actions"
                style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}
              >
                <button
                  type="button"
                  onClick={() =>
                    postReviewAction(
                      ACTIONS.CONFIRM,
                      "Confirmed valid by reviewer.",
                      "Sighting confirmed valid and removed from review."
                    )
                  }
                  style={{ ...buttonStyle, background: colors.success, color: "#fff" }}
                >
                  Confirm Valid
                </button>

                <button
                  type="button"
                  onClick={() =>
                    postReviewAction(
                      ACTIONS.CONFIRM_FOREIGN,
                      "Confirmed as a valid foreign plate by reviewer.",
                      "Foreign plate confirmed and removed from review."
                    )
                  }
                  style={{ ...buttonStyle, background: colors.primary, color: "#fff" }}
                >
                  Confirm Foreign Plate
                </button>

                <button
                  type="button"
                  onClick={correctPlateValue}
                  style={{ ...buttonStyle, background: colors.accent, color: "#fff" }}
                >
                  Correct Plate
                </button>

                <button
                  type="button"
                  onClick={retryDvla}
                  style={{ ...buttonStyle, background: colors.warning, color: "#fff" }}
                >
                  Retry DVLA
                </button>

                <button
                  type="button"
                  onClick={quarantine}
                  style={{ ...buttonStyle, background: colors.secondary, color: "#fff" }}
                >
                  Quarantine
                </button>

                <button
                  type="button"
                  onClick={restore}
                  style={{ ...buttonStyle, background: colors.primary, color: "#fff" }}
                >
                  Restore
                </button>

                <button
                  type="button"
                  onClick={() => postReviewAction(ACTIONS.NOTE)}
                  style={{ ...buttonStyle, background: "#f3f4f6", color: colors.text }}
                >
                  Add Note
                </button>

                <button
                  type="button"
                  onClick={hardDelete}
                  style={{ ...buttonStyle, background: colors.danger, color: "#fff" }}
                >
                  Hard Delete
                </button>
              </div>

              <div style={{ marginTop: "16px" }}>
                <h4 style={{ marginBottom: "8px", color: colors.primary }}>Audit Trail</h4>

                {detailLoading && <div style={{ color: colors.muted }}>Loading audit trail...</div>}

                {!detailLoading && detail?.review_actions?.length > 0 ? (
                  <div className="anpr-review-audit-list" style={{ display: "grid", gap: "8px" }}>
                    {detail.review_actions.map((action) => (
                      <div
                        key={action.id}
                        style={{
                          border: `1px solid ${colors.border}`,
                          borderRadius: "8px",
                          padding: "8px",
                          fontSize: "12px",
                        }}
                      >
                        <strong>{action.action}</strong>
                        <div style={{ color: colors.muted }}>{formatDateTime(action.reviewed_at)}</div>
                        {action.reason && <div>{action.reason}</div>}
                        {(action.old_plate_normalized || action.new_plate_normalized) && (
                          <div>
                            {action.old_plate_normalized || "—"} → {action.new_plate_normalized || "—"}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  !detailLoading && <div style={{ color: colors.muted }}>No review actions recorded.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function inputStyle(colors) {
  return {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "13px",
    outline: "none",
  };
}

function labelStyle(colors) {
  return {
    display: "block",
    marginTop: "10px",
    marginBottom: "4px",
    fontSize: "12px",
    fontWeight: 700,
    color: colors.muted,
  };
}

function Th({ children }) {
  return <th style={{ textAlign: "left", padding: "10px", whiteSpace: "nowrap" }}>{children}</th>;
}

function Td({ children, strong, colSpan, mobileLabel = "", mobileEmpty = false }) {
  return (
    <td
      colSpan={colSpan}
      data-mobile-label={mobileLabel}
      data-mobile-empty={mobileEmpty ? "true" : undefined}
      style={{
        padding: "9px 10px",
        whiteSpace: "nowrap",
        fontWeight: strong ? 700 : 400,
      }}
    >
      {children}
    </td>
  );
}

function Badge({ children, color }) {
  return (
    <span
      style={{
        display: "inline-block",
        borderRadius: "999px",
        padding: "3px 8px",
        background: color,
        color: "#fff",
        fontSize: "11px",
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

function AlertBox({ color, message }) {
  return (
    <div
      style={{
        border: `1px solid ${color}`,
        color,
        borderRadius: "8px",
        padding: "8px 10px",
        marginBottom: "10px",
        fontSize: "13px",
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 700 }}>{label}</div>
      <div>{value ?? "—"}</div>
    </div>
  );
}

function PreviewImage({ label, url }) {
  return (
    <div>
      <div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 700, marginBottom: "4px" }}>
        {label}
      </div>
      {url ? (
        <img
          src={url}
          alt={label}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.style.display = "none";
            const fallback = event.currentTarget.nextElementSibling;
            if (fallback) fallback.style.display = "flex";
          }}
          style={{
            width: "100%",
            height: "120px",
            objectFit: "contain",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            background: "#f9fafb",
          }}
        />
      ) : null}

      <div
        style={{
          height: "120px",
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          display: url ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
          background: "#f9fafb",
          fontSize: "12px",
          textAlign: "center",
          padding: "8px",
        }}
      >
        {url ? "Image unavailable" : "No image"}
      </div>
    </div>
  );
}